import { useEffect, useState } from "react";
import { Group } from "../types/types";
import { addWorkflowGroup, getGroup, getGroups, getGroupsWithParent, removeWorkflowGroup, updateGroup } from "../service/groupService";
import { showErrorToast, showSuccessToast } from "../utils/utils";

type useMoveFileProps = {
  onMove: () => void;
  workflowId?: number;
  groupId?: number;
  onError: (error: Error) => void;
}

const useMoveFile = (props: useMoveFileProps) => {

  const { workflowId, groupId, onError, onMove } = props
  const [groups, setGroups] = useState<Group[]>([])
  const [selectedGroup, setSelectedGroup] = useState<Group|undefined>(undefined)
  const [parentGroups, setParentGroups] = useState<Group[]>([])
  
  useEffect(() => {
    const getGroupsForParent = async () => {
      let res;
  
      if (parentGroups.length === 0) {
        res = await getGroups()
      } else {
        res = await getGroupsWithParent(parentGroups[parentGroups.length - 1].id)
      }
      if (res instanceof Error) {
        onError(res)
        return
      }
      setGroups(res as Group[])
    }
    getGroupsForParent()
  }, [parentGroups, onError])

  const onLoadSubGroups = (group?: Group) => {
    if (group === undefined) {
      // showErrorToast("Could not load subgroups.")
      setSelectedGroup(undefined)
      return
    }

    if (parentGroups.includes(group)) {
      const index = parentGroups.indexOf(group)
      setParentGroups(parentGroups.slice(0, index))
      setSelectedGroup(group)
      return
    }

    setParentGroups([...parentGroups, group])
    setSelectedGroup(group)
  }
  
  const onMoveWorkflow = async (parentGroupId?: number) => {
    if (workflowId === undefined) {
      showErrorToast("No workflow selected")
      return
    }

    if (selectedGroup === undefined) {      
      showErrorToast("No group selected")
      return
    }    
    const res = await addWorkflowGroup(selectedGroup.id, workflowId)
    if (res instanceof Error) {
      onError(res)
      return
    }
        
    if (parentGroupId !== undefined) {      
      const deleteWorkflowGroup = await removeWorkflowGroup(parentGroupId, workflowId)
      if (deleteWorkflowGroup instanceof Error) {
        onError(deleteWorkflowGroup)
        return
      }
    }
    showSuccessToast(`Workflow moved to ${selectedGroup.name}`)
    onMove()
  }

  const onMoveGroup = async () => {
    if (groupId === undefined) {
      showErrorToast("No group selected")
      return
    }

    // if (selectedGroup === undefined) {      
    //   showErrorToast("No group selected")
    //   return
    // }

    if (selectedGroup?.id === groupId) {
      showErrorToast("Cannot move group to itself.")
      return
    }

    const res = await getGroup(groupId)
    if (res instanceof Error) {
      onError(res)
      return
    }

    let parentId

    if (selectedGroup === undefined && parentGroups.length === 0) {
      parentId = {parent_id: null}
    } else {
      parentId = {parent_id: selectedGroup?.id}
    }

    const group = res as Group
    const updatedGroup = { ...group, ...parentId}
    
    const updateRes = await updateGroup(groupId, updatedGroup)
    if (updateRes instanceof Error) {
      onError(updateRes)
      return
    }
    showSuccessToast(`${group.name} moved successfully.`)
    onMove()
  }

  return [{
    groups,
    selectedGroup,
    parentGroups,
    onLoadSubGroups,
    onSelectGroup: (group: Group) => setSelectedGroup(group),
    onMoveWorkflow,
    onMoveGroup,
  }]

}

export default useMoveFile